import React, { useState, useEffect } from 'react';
import { X, Plus, Check, Trash2, Edit2, Coins, Calendar, CreditCard, AlertCircle, FileUp, Download, Paperclip } from 'lucide-react';
import projectsService from '../services/projectsService';
import toast from 'react-hot-toast';
import ConfirmModal from './ConfirmModal';

const PROJECT_EXTRA_INCOMES_STATUS = {
  PENDING: { label: 'Pendente', color: 'bg-yellow-100 text-yellow-800' },
  PARTIAL: { label: 'Parcial', color: 'bg-blue-100 text-blue-800' },
  COMPLETED: { label: 'Completo', color: 'bg-green-100 text-green-800' }
};

const PAYMENT_METHODS = [
  { value: 'dinheiro', label: 'Dinheiro' },
  { value: 'pix', label: 'PIX' },
  { value: 'transferencia', label: 'Transferência' },
  { value: 'cartao_debito', label: 'Cartão de Débito' },
  { value: 'cartao_credito', label: 'Cartão de Crédito' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'boleto', label: 'Boleto' }
];

const INSTALLMENT_OPTIONS = [
  { value: 1, label: '1x (à vista)' },
  { value: 2, label: '2x' },
  { value: 3, label: '3x' },
  { value: 4, label: '4x' },
  { value: 5, label: '5x' },
  { value: 6, label: '6x' },
  { value: 7, label: '7x' },
  { value: 8, label: '8x' },
  { value: 9, label: '9x' },
  { value: 10, label: '10x' },
  { value: 11, label: '11x' },
  { value: 12, label: '12x' }
];

const ProjectExtraIncomes = ({ project, onClose, onExtraIncomesChanged }) => {
  const [extraIncomes, setExtraIncomes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingExtra, setEditingExtra] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmingEntry, setConfirmingEntry] = useState(null);
  const [showFilesModal, setShowFilesModal] = useState(false);
  const [filesExtra, setFilesExtra] = useState(null);
  const [extraFiles, setExtraFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [confirmDeleteExtra, setConfirmDeleteExtra] = useState(null);
  const [confirmDeleteFile, setConfirmDeleteFile] = useState(null);

  const [formData, setFormData] = useState({
    description: '',
    total_amount: '',
    payment_method: 'pix',
    installments: 1,
    reference_date: ''
  });

  useEffect(() => {
    loadExtraIncomes();
  }, [project?.id]);

  const loadExtraIncomes = async () => {
    if (!project?.id) return;

    try {
      setLoading(true);
      const response = await projectsService.getProjectExtraIncomes(project.id);
      setExtraIncomes(response.data?.data || response.data || []);
    } catch (error) {
      console.error('Erro ao carregar recebimentos extras:', error);
      toast.error('Erro ao carregar recebimentos extras');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      description: '',
      total_amount: '',
      payment_method: 'pix',
      installments: 1,
      reference_date: ''
    });
    setEditingExtra(null);
  };

  const handleOpenForm = (extra = null) => {
    if (extra) {
      setEditingExtra(extra);
      setFormData({
        description: extra.description,
        total_amount: extra.total_amount,
        payment_method: extra.payment_method || 'pix',
        installments: extra.installments,
        reference_date: extra.reference_date
      });
    } else {
      resetForm();
    }
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    resetForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.description || !formData.total_amount || !formData.reference_date) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    try {
      setSaving(true);

      const data = {
        description: formData.description,
        total_amount: parseFloat(formData.total_amount),
        reference_date: formData.reference_date,
        payment_method: formData.payment_method,
        installments: parseInt(formData.installments)
      };

      if (editingExtra) {
        await projectsService.updateProjectExtraIncome(project.id, editingExtra.id, data);
        toast.success('Recebimento extra atualizado!');
      } else {
        await projectsService.createProjectExtraIncome(project.id, data);
        toast.success('Recebimento extra criado!');
      }

      handleCloseForm();
      loadExtraIncomes();
      if (onExtraIncomesChanged) onExtraIncomesChanged();
    } catch (error) {
      console.error('Erro ao salvar:', error);
      toast.error(error.response?.data?.message || 'Erro ao salvar recebimento extra');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (extra) => {
    setConfirmDeleteExtra(extra);
  };

  const handleConfirmDeleteExtra = async () => {
    const extra = confirmDeleteExtra;
    setConfirmDeleteExtra(null);

    try {
      await projectsService.deleteProjectExtraIncome(project.id, extra.id);
      toast.success('Recebimento extra excluído e caixa revertido!');
      loadExtraIncomes();
      if (onExtraIncomesChanged) onExtraIncomesChanged();
    } catch (error) {
      console.error('Erro ao excluir:', error);
      toast.error(error.response?.data?.message || 'Erro ao excluir recebimento extra');
    }
  };

  const handleConfirmEntry = async (extra, entry) => {
    if (entry.is_received) return;

    try {
      setConfirmingEntry(entry.id);
      await projectsService.confirmProjectExtraIncomeEntry(project.id, extra.id, entry.id);
      toast.success('Parcela confirmada com sucesso!');
      loadExtraIncomes();
    } catch (error) {
      console.error('Erro ao confirmar:', error);
      toast.error(error.response?.data?.message || 'Erro ao confirmar parcela');
    } finally {
      setConfirmingEntry(null);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    // Handle ISO format (2026-06-28T00:00:00.000Z) or simple format (2026-06-28)
    const dateOnly = dateStr.split('T')[0];
    if (!dateOnly.match(/^\d{4}-\d{2}-\d{2}$/)) return dateStr;
    const [year, month, day] = dateOnly.split('-');
    return `${day}/${month}/${year}`;
  };

  const getPaymentMethodLabel = (method) => {
    const labels = {
      'dinheiro': 'Dinheiro',
      'pix': 'PIX',
      'transferencia': 'Transferência',
      'cartao_debito': 'Cartão de Débito',
      'cartao_credito': 'Cartão de Crédito',
      'credit_card': 'Cartão de Crédito',
      'debit_card': 'Cartão de Débito',
      'bank_transfer': 'Transferência',
      'cheque': 'Cheque',
      'boleto': 'Boleto'
    };
    return labels[method] || method || '-';
  };

  const openFilesModal = async (extra) => {
    setFilesExtra(extra);
    setShowFilesModal(true);
    await loadExtraFiles(extra.id);
  };

  const loadExtraFiles = async (extraIncomeId) => {
    try {
      setLoadingFiles(true);
      const response = await projectsService.getExtraIncomeFiles(project.id, extraIncomeId);
      setExtraFiles(response.data?.data || response.data || []);
    } catch (error) {
      console.error('Erro ao carregar arquivos:', error);
      toast.error('Erro ao carregar arquivos');
    } finally {
      setLoadingFiles(false);
    }
  };

  const handleUploadFiles = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    try {
      setUploadingFiles(true);
      const formData = new FormData();
      for (let i = 0; i < files.length; i++) {
        formData.append('files', files[i]);
      }
      await projectsService.uploadExtraIncomeFiles(project.id, filesExtra.id, formData);
      toast.success(`${files.length} arquivo(s) enviado(s)!`);
      await loadExtraFiles(filesExtra.id);
    } catch (error) {
      console.error('Erro ao fazer upload:', error);
      toast.error(error.response?.data?.message || 'Erro ao fazer upload');
    } finally {
      setUploadingFiles(false);
      event.target.value = '';
    }
  };

  const handleDownloadFile = async (fileId, fileName) => {
    try {
      const response = await projectsService.downloadExtraIncomeFile(fileId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao baixar arquivo:', error);
      toast.error('Erro ao baixar arquivo');
    }
  };

  const handleDeleteFile = async (fileId) => {
    setConfirmDeleteFile(fileId);
  };

  const handleConfirmDeleteFile = async () => {
    const fileId = confirmDeleteFile;
    setConfirmDeleteFile(null);

    try {
      await projectsService.deleteExtraIncomeFile(fileId);
      toast.success('Arquivo excluído!');
      await loadExtraFiles(filesExtra.id);
    } catch (error) {
      console.error('Erro ao deletar arquivo:', error);
      toast.error('Erro ao deletar arquivo');
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-2/3 shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6 pb-4 border-b">
          <div>
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Coins className="w-6 h-6 text-yellow-600" />
              Recebimentos Extras
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {project?.project_code} - {project?.client_name}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleOpenForm()}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2 text-sm"
            >
              <Plus className="w-4 h-4" />
              Novo
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-2"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Form de Criação/Edição */}
        {showForm && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg border">
            <h3 className="font-semibold text-gray-900 mb-4">
              {editingExtra ? 'Editar Recebimento Extra' : 'Novo Recebimento Extra'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição *
                </label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                  placeholder="Ex: Sinal adicional, Frete, Montagem"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Valor Total (R$) *
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-2 text-gray-500">R$</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.total_amount}
                      onChange={(e) => setFormData({ ...formData, total_amount: e.target.value })}
                      className="w-full pl-10 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                      placeholder="0.00"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Forma de Pagamento
                  </label>
                  <select
                    value={formData.payment_method}
                    onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                  >
                    {PAYMENT_METHODS.map((method) => (
                      <option key={method.value} value={method.value}>
                        {method.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Parcelas
                  </label>
                  <select
                    value={formData.installments}
                    onChange={(e) => setFormData({ ...formData, installments: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                  >
                    {INSTALLMENT_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data de criação *
                  </label>
                  <input
                    type="date"
                    value={formData.reference_date}
                    onChange={(e) => setFormData({ ...formData, reference_date: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                    required
                  />
                </div>
              </div>

              {/* Simulação das parcelas */}
              {formData.total_amount && formData.reference_date && (
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm font-medium text-blue-800 mb-2">Simulação das Parcelas:</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {Array.from({ length: parseInt(formData.installments) }, (_, i) => {
                      const amount = (parseFloat(formData.total_amount) / parseInt(formData.installments)).toFixed(2);
                      const date = new Date(formData.reference_date);
                      date.setDate(date.getDate() + (i * 30));
                      return (
                        <div key={i} className="text-xs bg-white p-2 rounded border">
                          <span className="font-medium">Parcela {i + 1}:</span><br />
                          {formatCurrency(parseFloat(amount))}<br />
                          <span className="text-gray-500">{formatDate(date.toISOString().split('T')[0])}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseForm}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  {saving ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Lista de Recebimentos Extras */}
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
            <p className="text-gray-500 mt-2">Carregando...</p>
          </div>
        ) : extraIncomes.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Coins className="w-12 h-12 mx-auto mb-2 text-gray-300" />
            <p>Nenhum recebimento extra cadastrado</p>
          </div>
        ) : (
          <div className="space-y-4">
            {extraIncomes.map((extra) => {
              const statusInfo = PROJECT_EXTRA_INCOMES_STATUS[extra.status] || PROJECT_EXTRA_INCOMES_STATUS.PENDING;
              const receivedAmount = (extra.installments_paid || 0) * (extra.total_amount / extra.installments);

              return (
                <div key={extra.id} className="border rounded-lg p-4 bg-white hover:bg-gray-50">
                  {/* Header do Extra Income */}
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-gray-900">{extra.description}</h4>
                        <span className={`px-2 py-0.5 text-xs rounded-full ${statusInfo.color}`}>
                          {statusInfo.label}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">
                        {formatCurrency(extra.total_amount)} ({extra.installments}x)
                        <span className="ml-2 text-green-600">
                          {extra.installments_paid}/{extra.installments} pagas ({formatCurrency(receivedAmount)})
                        </span>
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => openFilesModal(extra)}
                        className="p-2 text-indigo-600 hover:bg-indigo-100 rounded"
                        title="Anexos"
                      >
                        <Paperclip className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(extra)}
                        className="p-2 text-red-600 hover:bg-red-100 rounded"
                        title="Excluir"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Lista de Parcelas */}
                  <div className="space-y-2">
                    {(extra.entries || []).map((entry) => (
                      <div
                        key={entry.id}
                        className={`flex items-center justify-between p-3 rounded-lg border ${
                          entry.is_received ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            entry.is_received ? 'bg-green-200 text-green-700' : 'bg-gray-200 text-gray-500'
                          }`}>
                            {entry.is_received ? (
                              <Check className="w-4 h-4" />
                            ) : (
                              <span className="text-xs font-medium">{extra.entries.indexOf(entry) + 1}</span>
                            )}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {formatDate(entry.due_date)}
                            </p>
                            <p className="text-xs text-gray-500">
                              {formatCurrency(entry.amount)}
                              <span className="ml-2 text-gray-400">
                                ({getPaymentMethodLabel(extra.payment_method)})
                              </span>
                              {entry.is_received && entry.received_date && (
                                <span className="ml-2 text-green-600">
                                  Confirmado em {formatDate(entry.received_date)}
                                </span>
                              )}
                            </p>
                          </div>
                        </div>
                        {!entry.is_received && (
                          <button
                            onClick={() => handleConfirmEntry(extra, entry)}
                            disabled={confirmingEntry === entry.id}
                            className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 disabled:opacity-50"
                          >
                            {confirmingEntry === entry.id ? '...' : 'Confirmar'}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

      {/* Modal de Arquivos Anexos */}
      {showFilesModal && filesExtra && (
        <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-[60]">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-1/2 shadow-lg rounded-md bg-white max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                <Paperclip className="w-5 h-5 text-indigo-600" />
                Arquivos Anexos - {filesExtra.description}
              </h3>
              <button
                onClick={() => {
                  setShowFilesModal(false);
                  setFilesExtra(null);
                  setExtraFiles([]);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Upload Area */}
            <div className="mb-4 p-4 border-2 border-dashed border-gray-300 rounded-lg text-center">
              <FileUp className="w-8 h-8 mx-auto mb-2 text-gray-400" />
              <label className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 cursor-pointer text-sm">
                <FileUp className="w-4 h-4" />
                Selecionar Arquivos
                <input
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
                  onChange={handleUploadFiles}
                  disabled={uploadingFiles}
                  className="hidden"
                />
              </label>
              {uploadingFiles && <p className="mt-2 text-sm text-indigo-600">Enviando...</p>}
            </div>

            {/* File List */}
            {loadingFiles ? (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600 mx-auto"></div>
              </div>
            ) : extraFiles.length === 0 ? (
              <div className="text-center py-8 text-gray-500 text-sm">
                Nenhum arquivo anexado
              </div>
            ) : (
              <div className="space-y-2">
                {extraFiles.map((file) => (
                  <div key={file.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2 overflow-hidden">
                      <FileUp className="w-5 h-5 text-gray-400 flex-shrink-0" />
                      <div className="overflow-hidden">
                        <p className="text-sm font-medium text-gray-900 truncate" title={file.original_name}>
                          {file.original_name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {(file.file_size / 1024).toFixed(1)} KB
                          {file.uploaded_by_name && ` | ${file.uploaded_by_name}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => handleDownloadFile(file.id, file.original_name)}
                        className="p-2 text-blue-600 hover:bg-blue-100 rounded"
                        title="Baixar"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteFile(file.id)}
                        className="p-2 text-red-600 hover:bg-red-100 rounded"
                        title="Excluir"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-end pt-4 border-t mt-4">
              <button
                onClick={() => {
                  setShowFilesModal(false);
                  setFilesExtra(null);
                  setExtraFiles([]);
                }}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="flex justify-end pt-4 border-t mt-6">
        <button
          onClick={onClose}
          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
        >
          Fechar
        </button>
      </div>

      <ConfirmModal
        show={!!confirmDeleteExtra}
        title="Excluir Recebimento Extra"
        message="Tem certeza que deseja excluir este recebimento extra? Esta ação irá reverter as entradas no caixa."
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
        onConfirm={handleConfirmDeleteExtra}
        onCancel={() => setConfirmDeleteExtra(null)}
      />

      <ConfirmModal
        show={!!confirmDeleteFile}
        title="Excluir Arquivo"
        message="Tem certeza que deseja excluir este arquivo?"
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
        onConfirm={handleConfirmDeleteFile}
        onCancel={() => setConfirmDeleteFile(null)}
      />
    </div>
  </div>
);
};

export default ProjectExtraIncomes;